/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { Address } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { createWineClubInput, wineClubAction } from '../../dataActions/wine-club-signup';
import WineClubConfirmation from './wine-club-confirmation';
import WineClubMembership from './wine-club-membership';
import WineClubShipping from './wine-club-shipping';
import { IWineClubData } from './wine-club.data';
import { IWineClubProps } from './wine-club.props.autogenerated';
import { addressMapType, componentsType, fieldsType, headingsType, IWineClubStepComponentProps} from './wine-club.types';

//==============================================================================
/**
 * WineClub component
 * @extends {React.Component<IWineClubProps<IWineClubData>>}
 */
//==============================================================================
@observer
class WineClub extends React.Component<IWineClubProps<IWineClubData>> {

    //==========================================================================
    // Variables
    //==========================================================================

    private fieldsDefault: fieldsType = {
        membership: '',
        shippingAddress: {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: 'Select'
        }
    };

    @observable private fields: fieldsType = this.fieldsDefault;

    private addressMap: addressMapType = [
        {
            moduleData: 'phone',
            serverData: 'Phone'
        },
        {
            moduleData: 'address1',
            serverData: 'Street'
        },
        {
            moduleData: 'city',
            serverData: 'City'
        },
        {
            moduleData: 'state',
            serverData: 'State'
        },
        {
            moduleData: 'zip',
            serverData: 'ZipCode'
        },
        {
            moduleData: 'country',
            serverData: 'ThreeLetterISORegionName'
        },
    ];

    @observable private submitSuccess: boolean = false;
    @observable private submitFailure: boolean = false;
    @observable private failureMessage: string | undefined;
    @observable private error: boolean = false;

    @observable private customerAddresses: Address[];
    @observable private isAddressSelected: boolean = false;
    @observable private selectedAddress: Address | undefined;

    @observable private step: string = 'membership';

    private components: componentsType = {
        membership: WineClubMembership,
        shipping: WineClubShipping,
        confirmation: WineClubConfirmation
    };

    private customerEmail?: string;

    //==========================================================================
    // Public Methods
    //==========================================================================

    public constructor(props: IWineClubProps<IWineClubData>) {
        super(props);
        const { context, data } = this.props;
        this.customerEmail = context && context.request && context.request.user && context.request.user.emailAddress;
        this.customerAddresses = (data.address && data.address.result) || [];

        this._renderValidation = this._renderValidation.bind(this);
        this._renderButton = this._renderButton.bind(this);
        this._renderSubmit = this._renderSubmit.bind(this);
        this._toggleStep = this._toggleStep.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this._handleAddressChange = this._handleAddressChange.bind(this);
        this._resetAddressSelect = this._resetAddressSelect.bind(this);
        this._handleNext = this._handleNext.bind(this);
        this._handleSubmit = this._handleSubmit.bind(this);
    }

    public render(): JSX.Element {
        const { config, context, resources } = this.props;
        const user = context && context.request && context.request.user;
        const StepComponent: React.ComponentType<IWineClubStepComponentProps> = this._renderStep(this.step);

        return (
            <div className={classname('wine-club', config.className)}>
                {config && config.heading && this._renderHeading(config.heading, 'wine-club-heading')}
                { // If form has already been successfully submitted, do not render form.
                    !this.submitSuccess ?
                        // If user is logged in, render form. If not, redirect to Sign In page.
                        (user.isAuthenticated ? (
                            <form onSubmit={this._handleSubmit}>
                                <StepComponent
                                    {...this.props}
                                    fields={this.fields}
                                    error={this.error}
                                    customerAddresses={this.customerAddresses}
                                    isAddressSelected={this.isAddressSelected}
                                    selectedAddress={this.selectedAddress}
                                    renderValidation={this._renderValidation}
                                    renderButton={this._renderButton}
                                    renderSubmit={this._renderSubmit}
                                    toggleStep={this._toggleStep}
                                    handleChange={this._handleChange}
                                    handleAddressChange={this._handleAddressChange}
                                    resetAddressSelect={this._resetAddressSelect}
                                    handleNext={this._handleNext}
                                />
                            </form>
                        ) : window.location.assign(user.signInUrl as string))
                    : null
                }
                {this.submitSuccess && resources && resources.wineClub_formSuccess && this._renderText(resources.wineClub_formSuccess, 'wine-club-form-success')}
                {this.submitFailure && this.failureMessage && this._renderText(this.failureMessage, 'wine-club-form-failure')}
            </div>
        );
    }

    //==========================================================================
    // Private Methods
    //==========================================================================

    // Renders built-in Heading component.
    private _renderHeading(heading: headingsType, className?: string): JSX.Element {
        return (
            <Heading className={className} headingTag={heading.tag} text={heading.text} />
        );
    }

    // Renders text component.
    private _renderText(text: string, className?: string): JSX.Element {
        return (
            <div className={className}>
                {text}
            </div>
        );
    }

    // Renders validation error messages.
    private _renderValidation(text: string, className?: string): JSX.Element {
        return (
            <div role='alert' className={className}>
                {text}
            </div>
        );
    }

    // Renders 'Back' and 'Next' buttons that toggles between steps.
    private _renderButton(
        text: string,
        step: string,
        toggle: Function,
        className?: string,
        validate?: Function,
        handleNext?: Function
    ): JSX.Element {
        return (
            <div
                role='button'
                className='wine-club-button'
                // This event handler will only fire for 'Next' buttons with a step validation function.
                // tslint:disable-next-line:jsx-no-lambda react-this-binding-issue
                onClick={() => handleNext && handleNext(validate)}
            >
                <a
                    role='button'
                    // If step validation does not pass, button will have 'disabled' class.
                    className={`${className} ${validate && !validate() ? 'disabled' : ''}`}
                    // If step validation does not pass, event handler will not fire toggle step.
                    // tslint:disable-next-line:jsx-no-lambda react-this-binding-issue
                    onClick={() => {
                        return validate ? validate() && toggle(step) : toggle(step);
                    }}
                >
                    {text}
                </a>
            </div>
        );
    }

    // Renders 'Submit' button that sends form data to backend.
    private _renderSubmit(text: string, className?: string): JSX.Element {
        return (
            <div className='wine-club-button'>
                <button className={className}>
                    {text}
                </button>
            </div>
        );
    }

    // Clears error messages when toggling between steps.
    private _toggleStep(step: string): void {
        this.error = false;
        this.submitSuccess = false;
        this.submitFailure = false;
        this.step = step;
    }

    // Renders active step.
    private _renderStep(step: string): React.ComponentType<IWineClubStepComponentProps> {
        return this.components[step];
    }

    // Updates form object variable with updated values on input change.
    @action
    private _handleChange(event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void {
        event.target.getAttribute('data-group')
        ? this.fields[event.target.getAttribute('data-group') as string][event.target.id] = event.target.value
        : this.fields[event.target.id] = event.target.value;
    }

    // Changes form shipping address object variable with selected address values or clears if undefined.
    @action
    private _handleAddressChange(event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void {
        if (!this.isAddressSelected) {this.isAddressSelected = true;}
        const selectedAddress: Address | undefined = this.customerAddresses.find(address => address.FullAddress === event.target.value);
        if (selectedAddress) {
            this.selectedAddress = selectedAddress;
            this._handleAddressUpdate(this.selectedAddress);
        } else {
            this.selectedAddress = undefined;
            this._handleAddressClear();
            this._handleAssignEmail();
        }
    }

    // Updates form shipping address object variable with selected address values.
    @action
    private _handleAddressUpdate(address: Address): void {
        this.addressMap.forEach(addressMapContent => {
            address[addressMapContent.serverData]
            ? this.fields.shippingAddress[addressMapContent.moduleData] = address[addressMapContent.serverData]
            : '';
        });

        // To be fixed in the future. For now, we need to split the full name to be accepted by the server.
        const fullName = address.Name;
        const fullNameArray = fullName!.split(' ');
        const firstName = fullNameArray[0];
        const lastName = fullNameArray.slice(1).join(' ');

        this.fields.shippingAddress.firstName = firstName;
        this.fields.shippingAddress.lastName = lastName;
        this._handleAssignEmail();
    }

    // Clears form shipping address object variable.
    @action
    private _handleAddressClear(): void {
        this.fields.shippingAddress = this.fieldsDefault.shippingAddress;
    }

    // Automatically assigns form shipping address email to user's email.
    @action
    private _handleAssignEmail(): void {
        this.fields.shippingAddress.email = this.customerEmail!;
    }

    // Resets address select boolean to revert default dropdown value.
    @action
    private _resetAddressSelect(): void {
        this.isAddressSelected = false;
        this.selectedAddress = undefined;
        this._handleAddressClear();
    }

    // Runs active step's validation function and sets error variable to allow/restrict 'Next' button.
    private _handleNext(validate: Function): void {
        validate() ? this.error = false : this.error = true;
    }

    // Submits form and sends form object variable data to retail server.
    private _handleSubmit(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): void {
        event.preventDefault();

        // Resets submit success and failure messages in case user clicks button again without rerendering.
        this.submitSuccess = false;
        this.submitFailure = false;

        const { config, context, resources } = this.props;
        const { shippingAddress } = this.fields;
        const wineClubId: string = config && config.clubId;
        const customerId: string = context && context.request && context.request.user && context.request.user.customerAccountNumber!;
        const isGifted: boolean = this.fields && this.fields.membership.includes('gift');

        const input = createWineClubInput({
            wineClubId: wineClubId,
            customerId: customerId,
            isGifted: isGifted,
            deliveryPreference: 'DELIVERY',
            addressFirstName: shippingAddress.firstName,
            addressLastName: shippingAddress.lastName,
            addressEmail: shippingAddress.email,
            addressPhone: shippingAddress.phone,
            addressLine1: shippingAddress.address1,
            addressLine2: shippingAddress.address2 || '',
            city: shippingAddress.city,
            state: shippingAddress.state,
            zipcode: shippingAddress.zip,
            country: shippingAddress.country
        });

        wineClubAction(input, this.props.context.actionContext)
        .then(response => {
            if (response.result) {
                this.submitSuccess = true;
            } else {
                this.submitFailure = true;
                this.failureMessage = response.Message || (resources && resources.wineClub_formFailure);
            }
        })
        .catch(error => {
            throw new Error(JSON.stringify(error, null, 4));
        });
    }
}

export default WineClub;